import validateCategoryParameters from './validateCategoryParameters';
import { default as itemsCategoriesStoreUpdate } from 'owa-mail-actions/lib/triage/itemsCategoriesStoreUpdate';
import { getRowIdsFromRowKeys, TableView } from 'owa-mail-list-store';
import { ActionSource, selectMailStoreItemById } from 'owa-mail-store';
import * as undoActions from 'owa-mail-undo';
import updateItemsCategoriesService, {
    UpdateCategoriesItemContext,
} from '../../../services/updateItemsCategoriesService';
import tombstoneOperations, { TombstoneReasonType } from 'owa-mail-list-tombstone';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import {
    handleServerResponseSuccessAndError,
    onTriageActionCompleted,
    onTriageActionFailed,
} from 'owa-mail-triage-action-response-processor';

/**
 * Internal method to update categories
 * @param itemRowKeys the row keys of the items
 * @param tableView that contains the items
 * @param categoriesToAdd the categories to be added to the items
 * @param categoriesToRemove the categories to be removed from the items
 * @param clearCategories whether to clear categories
 * @param actionSource where this action is triggered from
 * @param promiseToWaitUpon promise to wait upon before sending the update category request.
 */
export default function updateItemsCategoriesInternal(
    itemRowKeys: string[],
    tableView: TableView,
    categoriesToAdd: string[],
    categoriesToRemove: string[],
    clearCategories: boolean,
    actionSource: ActionSource,
    promiseToWaitUpon: Promise<boolean>
): Promise<void> {
    // Validate category parameters and throw error if they are invalid
    validateCategoryParameters(itemRowKeys, categoriesToAdd, categoriesToRemove, clearCategories);
    undoActions.clearLastUndoableAction();

    if (promiseToWaitUpon) {
        // In case where user creates a category from the commanding bar drop down
        // we want to only send the request to apply the category only if the creation of
        // category succeeds. Today the promiseToWaitUpon here is create Category action
        return promiseToWaitUpon.then(isPromiseSuccessful => {
            return submitRequestAndMakeLocalUpdate(
                itemRowKeys,
                tableView,
                categoriesToAdd,
                categoriesToRemove,
                clearCategories
            );
        });
    } else {
        return submitRequestAndMakeLocalUpdate(
            itemRowKeys,
            tableView,
            categoriesToAdd,
            categoriesToRemove,
            clearCategories
        );
    }
}

function submitRequestAndMakeLocalUpdate(
    itemRowKeys: string[],
    tableView: TableView,
    categoriesToAdd: string[],
    categoriesToRemove: string[],
    clearCategories: boolean
) {
    const itemIdsToUpdate = getRowIdsFromRowKeys(itemRowKeys, tableView.id);

    // 1. Update the items categories in store.
    // After items categories got updated we can submit changes to the server.
    itemsCategoriesStoreUpdate(
        itemIdsToUpdate,
        categoriesToAdd,
        categoriesToRemove,
        clearCategories
    );

    const itemContexts = createCategoryServiceItemContexts(itemIdsToUpdate);

    // 2. Add category operation to tombstone
    // The local update operation should happen before adding category to tombstone
    // else we may not have correct set of categories when adding it to the tombstone
    tombstoneOperations.add(itemRowKeys, tableView.serverFolderId, TombstoneReasonType.Category);

    // 3. submit server request
    return submitUpdateItemCategories(itemRowKeys, tableView, itemContexts);
}

function submitUpdateItemCategories(
    itemRowKeys: string[],
    tableView: TableView,
    itemContexts: UpdateCategoriesItemContext[]
): Promise<void> {
    const options = getMailboxInfo(tableView).requestOptions;

    // Issue service request
    return updateItemsCategoriesService(itemContexts, options)
        .then(response => {
            onTriageActionCompleted(itemRowKeys, tableView.serverFolderId, true);
            return handleServerResponseSuccessAndError(response);
        })
        .catch(error => {
            onTriageActionCompleted(itemRowKeys, tableView.serverFolderId, true);
            onTriageActionFailed(tableView);
            return Promise.reject(error);
        });
}

/**
 * Create item contexts for update item category service
 * @param itemIds to be updated
 * @returns an array of UpdateCategoriesItemContext which is used for the service
 */
function createCategoryServiceItemContexts(
    itemIdsToUpdate: string[]
): UpdateCategoriesItemContext[] {
    const itemContexts: UpdateCategoriesItemContext[] = [];
    itemIdsToUpdate.forEach(itemId => {
        const item = selectMailStoreItemById(itemId);
        itemContexts.push({
            itemId: item.ItemId,
            instanceKey: item.InstanceKey,
            newCategoriesCollection: item.Categories,
        });
    });

    return itemContexts;
}
